// schedule-engine.js — parrilla / “now playing” (admin agenda, watch modal)
import './sync-epoch.js';

function syncedNowSeconds() {
  const api = typeof window !== 'undefined' ? window.RAPTV_SYNC : null;
  return api ? api.getSyncedNowSeconds() : Date.now() / 1000;
}

/**
 * @param {Object} ch - Channel object with videos array
 * @returns {Object|null} Current video info with elapsed time
 */
export function getNowPlaying(ch) {
  const videos = ch.videos.filter((v) => (v.duration || 0) > 0);
  if (!videos.length) return null;

  const api = typeof window !== 'undefined' ? window.RAPTV_SYNC : null;
  const total = api
    ? api.totalDuration(videos, (v) => v.duration || 0)
    : videos.reduce((s, v) => s + v.duration, 0);
  if (!total) return null;

  const pos = syncedNowSeconds() % total;
  const slot = api
    ? api.findSlotAtElapsed(pos, videos, (v) => v.duration || 0)
    : _findSlotFallback(pos, videos);

  const vid = videos[slot.videoIndex];
  if (!vid) return null;
  return { vid, elapsed: slot.offsetInVideo, total: vid.duration };
}

function _findSlotFallback(pos, videos) {
  let acc = 0;
  for (let i = 0; i < videos.length; i++) {
    const d = videos[i].duration;
    if (pos < acc + d) return { videoIndex: i, offsetInVideo: pos - acc };
    acc += d;
  }
  return { videoIndex: 0, offsetInVideo: 0 };
}

/**
 * @param {Object} ch - Channel object with videos array
 * @param {number} maxT - Maximum schedule duration in seconds
 * @returns {Array} Schedule slots with timing info
 */
export function computeDaySchedule(ch, maxT = 86400) {
  const videos = ch.videos.filter((v) => (v.duration || 0) > 0);
  const totalDuration = videos.reduce((s, v) => s + v.duration, 0);
  if (!totalDuration) return [];

  const now = syncedNowSeconds();
  const todayMidnight = Math.floor(now / 86400) * 86400;
  const secsNow = now - todayMidnight;
  const result = [];
  let posInCycle = todayMidnight % totalDuration;
  let t = 0;

  while (t < maxT) {
    let acc = 0;
    let vidIdx = 0;
    let vidStart = 0;
    for (let i = 0; i < videos.length; i++) {
      const d = videos[i].duration;
      if (posInCycle < acc + d) {
        vidIdx = i;
        vidStart = acc;
        break;
      }
      acc += d;
      if (i === videos.length - 1) {
        vidIdx = i;
        vidStart = acc - d;
      }
    }
    const vid = videos[vidIdx];
    const remaining = vid.duration - (posInCycle - vidStart);
    const slotDur = Math.min(remaining, maxT - t);
    if (slotDur <= 0) break;

    const isNow = secsNow >= t && secsNow < t + slotDur;
    const isPast = secsNow >= t + slotDur;
    result.push({ t, slotDur, vid, isNow, isPast, elapsed: isNow ? secsNow - t : 0 });
    t += slotDur;
    posInCycle = (posInCycle + slotDur) % totalDuration;
  }
  return result;
}
